import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Fab,
  Zoom,
  Tooltip,
  Chip,
  Stack,
  Collapse,
} from "@mui/material";
import { Send, Close, DeleteOutline, ExpandMore } from "@mui/icons-material";
import SmartToyIcon from "@mui/icons-material/SmartToy";

const SUGGESTIONS = [
  "Tell me about your projects",
  "What are your technical skills?",
  "Describe your work experience",
  "What hackathons have you won?",
];

const SESSION_KEY = "abhi_chat_history";

/** Animated typing dots */
const TypingIndicator = () => (
  <Box
    sx={{
      alignSelf: "flex-start",
      display: "flex",
      gap: 0.5,
      p: 1.5,
      bgcolor: "#2A2A2A",
      borderRadius: 2,
      borderBottomLeftRadius: 0,
    }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "#7C3AED",
          animation: "typingBounce 1.4s infinite ease-in-out",
          animationDelay: `${i * 0.2}s`,
          "@keyframes typingBounce": {
            "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
            "40%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
);

/** Collapsible source citations */
const SourceCitations = ({ sources }) => {
  const [expanded, setExpanded] = useState(false);
  if (!sources || sources.length === 0) return null;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          color: "#888",
          "&:hover": { color: "#aaa" },
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
          Sources ({sources.length})
        </Typography>
        <ExpandMore
          sx={{
            fontSize: 14,
            transform: expanded ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.2s",
            ml: 0.3,
          }}
        />
      </Box>
      <Collapse in={expanded}>
        <Stack gap={0.3} sx={{ mt: 0.5 }}>
          {sources.map((src, i) => (
            <Typography
              key={i}
              variant="caption"
              sx={{
                fontSize: "0.6rem",
                color: "#666",
                pl: 1,
                borderLeft: "2px solid #7C3AED33",
              }}
            >
              {src.section}: {src.title}
            </Typography>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

const ChatBox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Persist chat to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(chatHistory));
    } catch {
      // sessionStorage full or unavailable — ignore
    }
  }, [chatHistory]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, isOpen, scrollToBottom]);

  /**
   * Send a message. Tries streaming (SSE) first, falls back to JSON.
   */
  const handleSendMessage = async (text = message) => {
    if (!text.trim() || isLoading) return;

    const userMsg = { type: "user", text: text.trim() };
    setChatHistory((prev) => [...prev, userMsg]);
    setMessage("");
    setIsLoading(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ question: text.trim() }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          errData.error || "Something went wrong. Please try again."
        );
      }

      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("text/event-stream")) {
        // --- Streaming SSE ---
        await handleStreamingResponse(response);
      } else {
        // --- JSON fallback (greetings, cached) ---
        const data = await response.json();
        setChatHistory((prev) => [
          ...prev,
          {
            type: "bot",
            text: data.answer || "I'm not sure how to respond to that.",
            sources: data.sources || [],
          },
        ]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setChatHistory((prev) => [
        ...prev,
        {
          type: "bot",
          text:
            error.message || "Sorry, I'm having trouble connecting right now.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Process a single SSE event, updating accumulated state via refs.
   */
  const processSSEEvent = useCallback((event, botTextRef, sourcesRef) => {
    if (event.type === "token") {
      botTextRef.current += event.content;
      const currentText = botTextRef.current;
      setChatHistory((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.streaming) {
          updated[updated.length - 1] = { ...last, text: currentText };
        }
        return updated;
      });
    } else if (event.type === "sources") {
      sourcesRef.current = event.sources || [];
    } else if (event.type === "done") {
      const finalText = botTextRef.current;
      const finalSources = sourcesRef.current;
      setChatHistory((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.streaming) {
          updated[updated.length - 1] = {
            ...last,
            text: finalText,
            sources: finalSources,
            streaming: false,
          };
        }
        return updated;
      });
    } else if (event.type === "error") {
      const errText = event.message || "An error occurred.";
      botTextRef.current = errText;
      setChatHistory((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.streaming) {
          updated[updated.length - 1] = {
            ...last,
            text: errText,
            streaming: false,
          };
        }
        return updated;
      });
    }
  }, []);

  /**
   * Process a streaming SSE response, updating the bot message progressively.
   */
  const handleStreamingResponse = async (response) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const botTextRef = { current: "" };
    const sourcesRef = { current: [] };
    let buffer = "";

    // Add placeholder bot message
    setChatHistory((prev) => [
      ...prev,
      { type: "bot", text: "", sources: [], streaming: true },
    ]);

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE lines
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            processSSEEvent(event, botTextRef, sourcesRef);
          } catch {
            // Skip malformed SSE events
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setChatHistory([]);
    sessionStorage.removeItem(SESSION_KEY);
  };

  return (
    <>
      <Tooltip title="Chat with AI" arrow placement="left">
        <Fab
          color="primary"
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 1000,
            bgcolor: isOpen ? "#f44336" : "#7C3AED",
            color: "#121212",
            "&:hover": { bgcolor: isOpen ? "#d32f2f" : "#6D28D9" },
          }}
        >
          {isOpen ? <Close /> : <SmartToyIcon />}
        </Fab>
      </Tooltip>

      <Zoom in={isOpen}>
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            bottom: 90,
            right: 20,
            width: { xs: "calc(100% - 32px)", sm: 380 },
            height: { xs: 450, sm: 520 },
            display: "flex",
            flexDirection: "column",
            zIndex: 999,
            borderRadius: 3,
            bgcolor: "#1E1E1E",
            border: "1px solid #333",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: "#121212",
              borderBottom: "1px solid #333",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <SmartToyIcon sx={{ color: "#7C3AED" }} />
              <Typography variant="subtitle1" color="white" fontWeight="bold">
                Abhi's Assistant
              </Typography>
            </Box>
            <Tooltip title="Clear Chat">
              <IconButton
                size="small"
                onClick={handleClearChat}
                sx={{ color: "#666" }}
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flexGrow: 1,
              p: 2,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#333",
                borderRadius: "3px",
              },
            }}
          >
            {chatHistory.length === 0 ? (
              <Box sx={{ mt: 4, textAlign: "center" }}>
                <Typography variant="body2" color="gray" mb={2}>
                  Hi! I'm Abhi's AI assistant. Ask me anything about his work,
                  projects, or experience!
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="center"
                  gap={1}
                >
                  {SUGGESTIONS.map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion}
                      onClick={() => handleSendMessage(suggestion)}
                      clickable
                      size="small"
                      sx={{
                        bgcolor: "rgba(124, 58, 237, 0.1)",
                        color: "#7C3AED",
                        border: "1px solid rgba(124, 58, 237, 0.3)",
                        fontSize: "0.75rem",
                        "&:hover": { bgcolor: "rgba(124, 58, 237, 0.2)" },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : (
              chatHistory.map((chat, index) => (
                <Box
                  key={index}
                  sx={{
                    alignSelf:
                      chat.type === "user" ? "flex-end" : "flex-start",
                    maxWidth: "85%",
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: chat.type === "user" ? "#7C3AED" : "#2A2A2A",
                      color: chat.type === "user" ? "#121212" : "#E0E0E0",
                      p: 1.5,
                      borderRadius: 2,
                      borderBottomRightRadius: chat.type === "user" ? 0 : 2,
                      borderBottomLeftRadius: chat.type === "bot" ? 0 : 2,
                      boxShadow: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}
                    >
                      {chat.text}
                      {chat.streaming && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            width: 2,
                            height: "1em",
                            bgcolor: "#7C3AED",
                            ml: 0.3,
                            animation: "cursorBlink 1s step-end infinite",
                            verticalAlign: "text-bottom",
                            "@keyframes cursorBlink": {
                              "0%, 100%": { opacity: 1 },
                              "50%": { opacity: 0 },
                            },
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                  {chat.type === "bot" && !chat.streaming && (
                    <SourceCitations sources={chat.sources} />
                  )}
                </Box>
              ))
            )}
            {isLoading &&
              !chatHistory.some((m) => m.streaming) && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2, bgcolor: "#121212", borderTop: "1px solid #333" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask about projects, skills, experience..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    bgcolor: "#1E1E1E",
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: "#333" },
                    "&:hover fieldset": { borderColor: "#555" },
                    "&.Mui-focused fieldset": { borderColor: "#7C3AED" },
                  },
                }}
              />
              <IconButton
                onClick={() => handleSendMessage()}
                disabled={isLoading || !message.trim()}
                sx={{
                  bgcolor: "#7C3AED",
                  color: "#121212",
                  "&:hover": { bgcolor: "#6D28D9" },
                  "&.Mui-disabled": { bgcolor: "#333", color: "#555" },
                }}
              >
                <Send fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Zoom>
    </>
  );
};

export default ChatBox;
